import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BookMarked, Plus, CheckCircle2, Archive, Users } from "lucide-react";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const CMD = ["supervisor", "watch_commander", "records_supervisor", "admin"];

export default function Policies() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ id: "", title: "", category: "sop", body: "" });
  const [open, setOpen] = useState(null);      // expanded policy detail {policy, acks}
  const canEdit = CMD.includes(user?.role);

  const load = useCallback(() => client.get("/policies").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    try {
      await client.post("/policies", form);
      toast.success(form.id ? "New version published — re-acknowledgment required" : "Policy published");
      setForm({ id: "", title: "", category: "sop", body: "" }); setOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const ack = async (p) => {
    try { await client.post(`/policies/${p.id}/ack`); toast.success(`Acknowledged ${p.number} v${p.version}`); load(); if (open?.policy?.id === p.id) detail(p); }
    catch (e) { toast.error(e.response?.data?.detail || "Ack failed"); }
  };
  const archive = async (p) => {
    if (!window.confirm(`Archive ${p.number} — ${p.title}?`)) return;
    try { await client.post(`/policies/${p.id}/archive`); toast.success("Archived"); setOpen(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };
  const detail = async (p) => {
    try { const { data: d } = await client.get(`/policies/${p.id}`); setOpen(d); }
    catch { toast.error("Load failed"); }
  };

  return (
    <Layout>
      <PageHeader title="Policy & SOP Control Center" sub="Versioned directives · read/acknowledge tracking · new versions require re-acknowledgment" icon={BookMarked} />
      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="policies-page">
        {canEdit && (
          <div className="border border-[#2C2D35] rounded-sm">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">
              {form.id ? `PUBLISH NEW VERSION — everyone must re-acknowledge` : "PUBLISH POLICY / SOP"}
            </div>
            <div className="p-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                <input data-testid="policy-title" className={`${inp} w-72`} value={form.title} placeholder="Title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <select data-testid="policy-category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {(data?.categories || []).map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </select>
                {form.id && <button data-testid="policy-cancel-version" onClick={() => setForm({ id: "", title: "", category: "sop", body: "" })} className="px-2 py-1 text-[9px] font-head font-bold border border-[#2C2D35] text-[#9CA3AF] rounded-sm uppercase">Cancel version</button>}
              </div>
              <textarea data-testid="policy-body" className={`${inp} w-full h-28`} value={form.body} placeholder="Policy text…" onChange={(e) => setForm({ ...form, body: e.target.value })} />
              <button data-testid="policy-save" onClick={save} className="flex items-center gap-1 px-3 py-2 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm"><Plus size={12} /> {form.id ? "PUBLISH VERSION" : "PUBLISH"}</button>
            </div>
          </div>
        )}

        <div className="border border-[#2C2D35] rounded-sm">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">ACTIVE DIRECTIVES</div>
          {(data?.policies || []).length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No policies published yet.</div>}
          {(data?.policies || []).map((p) => (
            <div key={p.id} className="border-b border-[#1E1F24]" data-testid={`policy-row-${p.number}`}>
              <div className="flex items-center gap-2 px-3 py-2 text-[11px] flex-wrap">
                <span className="font-mono text-[10px] text-[#22D3EE]">{p.number}</span>
                <button onClick={() => (open?.policy?.id === p.id ? setOpen(null) : detail(p))} className="text-white font-semibold hover:text-[#22D3EE] text-left" data-testid={`policy-open-${p.number}`}>{p.title}</button>
                <span className="font-mono text-[9px] text-[#6B7280] uppercase">{p.category.replace(/_/g, " ")} · v{p.version}</span>
                <span className="font-mono text-[9px] text-[#6B7280] flex items-center gap-1 ml-auto"><Users size={10} /> {p.ack_count} ack{p.ack_count === 1 ? "" : "s"}</span>
                {p.my_ack
                  ? <span data-testid={`policy-acked-${p.number}`} className="flex items-center gap-1 font-mono text-[9px] font-bold text-[#22C55E] uppercase"><CheckCircle2 size={11} /> Acknowledged</span>
                  : <button data-testid={`policy-ack-${p.number}`} onClick={() => ack(p)} className="px-2 py-1 text-[9px] font-head font-bold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#4ade80] rounded-sm uppercase">Acknowledge v{p.version}</button>}
                {canEdit && <>
                  <button data-testid={`policy-newver-${p.number}`} onClick={() => { setForm({ id: p.id, title: p.title, category: p.category, body: p.body }); window.scrollTo(0, 0); }} className="px-2 py-1 text-[9px] font-head font-bold bg-[#3B82F6]/15 border border-[#3B82F6]/50 text-[#60A5FA] rounded-sm uppercase">New version</button>
                  <button data-testid={`policy-archive-${p.number}`} onClick={() => archive(p)} className="text-[#EF4444]" title="Archive"><Archive size={12} /></button>
                </>}
              </div>
              {open?.policy?.id === p.id && (
                <div className="px-4 pb-3 space-y-2" data-testid={`policy-detail-${p.number}`}>
                  <pre className="font-mono text-[11px] text-[#D1D5DB] whitespace-pre-wrap bg-[#0d0e11] border border-[#2C2D35] p-3 rounded-sm">{open.policy.body}</pre>
                  <div className="font-mono text-[9px] text-[#6B7280] uppercase">Acknowledged v{open.policy.version}:</div>
                  {open.acks.length === 0 && <div className="font-mono text-[10px] text-[#F59E0B]">Nobody has acknowledged this version yet.</div>}
                  <div className="flex flex-wrap gap-1.5">
                    {open.acks.map((a, i) => (
                      <span key={i} className="font-mono text-[9px] px-1.5 py-0.5 border border-[#2C2D35] rounded-sm text-[#9CA3AF]">{a.by} · {(a.ts || "").slice(0, 10)}</span>))}
                  </div>
                </div>
              )}
            </div>))}
        </div>
      </div>
    </Layout>
  );
}
